import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { IPatientDemographics } from '@zero-dash/types';
import { defaultLeukemiaSubtypes, defaultMolecularConfirmation } from '@/constants/Curation/MolecularConfirmation';
import { LoadingPage } from '@/pages/Loading/Loading';
import { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import { IBiosample } from '@/types/Analysis/Biosamples.types';
import { IHTSCulture } from '@/types/HTS.types';
import { VariantType } from '@/types/misc.types';
import { ILeukemiaSubtypes, IMolecularConfirmation } from '@/types/MolecularConfirmation.types';
import { IPurity } from '@/types/Precuration/Purity.types';
import { IImmunoprofile, IRNASeqMetrics, ISeqMetrics } from '@/types/Precuration/QCMetrics.types';
import { BiosampleKind, isBiosampleOfKind } from '@/utils/functions/biosamples/biosampleKinds';
import { getGermlineBiosample } from '@/utils/functions/biosamples/getGermlineBiosample';
import { getHtsBiosamples } from '@/utils/functions/biosamples/getHtsBiosamples';
import { getMethBiosample } from '@/utils/functions/biosamples/getMethBiosample';
import getPrimaryBiosample from '@/utils/functions/biosamples/getPrimaryBiosample';
import { getRnaBiosample } from '@/utils/functions/biosamples/getRnaBiosample';
import { getTumourBiosample } from '@/utils/functions/biosamples/getTumourBiosample';
import { useZeroDashSdk } from './ZeroDashSdkContext';
import { useIsPatientReadOnly } from '@/hooks/useIsPatientReadOnly';

export interface IAnalysisSetContext {
  analysisSet: IAnalysisSet;
  setAnalysisSet: Dispatch<SetStateAction<IAnalysisSet>>;
  demographics?: IPatientDemographics | null;
  updateDemographics: (demographics?: IPatientDemographics | null) => void;
  primaryBiosample?: IBiosample;
  tumourBiosample?: IBiosample;
  germlineBiosample?: IBiosample;
  rnaBiosample?: IBiosample;
  methBiosample?: IBiosample;
  htsBiosamples?: IBiosample[];
  htsCultures?: IHTSCulture[];
  getBiosampleForVariantType: (variantType: VariantType) => IBiosample | undefined;
  // Makes the curation tabs read from this biosample instead of the default one of its kind
  selectBiosample: (biosampleId: string) => void;
  onCultureChange?: (
    biosampleId: string,
    screenName: string,
    newCulture: IHTSCulture,
  ) => void
  purity?: IPurity;
  metrics?: ISeqMetrics[];
  rnaseqMetrics?: IRNASeqMetrics;
  immunoprofile?: IImmunoprofile;
  molecularConfirmation: IMolecularConfirmation | undefined;
  setMolecularConfirmation: Dispatch<SetStateAction<IMolecularConfirmation| undefined>>;
  leukemiaSubtypes: ILeukemiaSubtypes | undefined;
  setLeukemiaSubtypes: Dispatch<SetStateAction<ILeukemiaSubtypes| undefined>>;
  isReadOnly: boolean;
  errorLoadingItems: string[];
}

// The kinds of biosample a user can switch between on the Curation tab
const selectableKinds = ['tumour', 'germline', 'rna', 'methylation'] as const satisfies readonly BiosampleKind[];
type SelectableKind = typeof selectableKinds[number];

export const AnalysisSetContext = createContext<IAnalysisSetContext | undefined>(undefined);
AnalysisSetContext.displayName = 'AnalysisSetContext';

export const useAnalysisSet = (): IAnalysisSetContext => {
  const ctx = useContext(AnalysisSetContext);

  if (!ctx) {
    throw new Error('Analysis set context is not available at this scope');
  }

  return ctx;
};

interface IProps {
  children?: ReactNode;
  analysisSetId?: string;
}

export function AnalysisSetProvider({
  children,
  analysisSetId: inputAnalysisSetId,
}: IProps): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const { enqueueSnackbar } = useSnackbar();
  const { analysisSetId: paramAnlysisSetId } = useParams();
  const analysisSetId = inputAnalysisSetId ?? paramAnlysisSetId;
  const [analysisSet, setAnalysisSet] = useState<IAnalysisSet>({} as IAnalysisSet);
  const [demographics, setDemographics] = useState<IPatientDemographics | null>();
  const [purity, setPurity] = useState<IPurity>();
  const [metrics, setMetrics] = useState<ISeqMetrics[]>();
  const [rnaseqMetrics, setRnaseqMetrics] = useState<IRNASeqMetrics>();
  const [immunoprofile, setImmunoprofile] = useState<IImmunoprofile>();
  const [htsCultures, setHTSCultures] = useState<IHTSCulture[]>([]);
  const [errorLoadingItems, setErrorLoadingItems] = useState<string[]>([]);
  const [
    molecularConfirmation,
    setMolecularConfirmation,
  ] = useState<IMolecularConfirmation | undefined>();
  const [
    leukemiaSubtypes,
    setLeukemiaSubtypes,
  ] = useState<ILeukemiaSubtypes | undefined>();
  const [
    selectedBiosampleIds,
    setSelectedBiosampleIds,
  ] = useState<Partial<Record<SelectableKind, string>>>({});
  const isReadOnly = useIsPatientReadOnly({ analysisSetId });

  // The biosample each kind reads from: the user's choice when it is still in the set,
  // otherwise the default one of that kind
  const kindBiosamples = useMemo(() => {
    const biosamples = analysisSet.biosamples || [];
    const pick = (kind: SelectableKind, fallback?: IBiosample): IBiosample | undefined => (
      biosamples.find((b) => (
        b.biosampleId === selectedBiosampleIds[kind] && isBiosampleOfKind[kind](b)
      )) ?? fallback
    );
    return {
      tumour: pick('tumour', getTumourBiosample(biosamples)),
      germline: pick('germline', getGermlineBiosample(biosamples)),
      rna: pick('rna', getRnaBiosample(biosamples)),
      methylation: pick('methylation', getMethBiosample(biosamples)),
    };
  }, [analysisSet.biosamples, selectedBiosampleIds]);

  const selectBiosample = useCallback((biosampleId: string): void => {
    const biosample = analysisSet.biosamples?.find((b) => b.biosampleId === biosampleId);
    if (!biosample) return;

    const kinds = selectableKinds.filter((kind) => isBiosampleOfKind[kind](biosample));
    setSelectedBiosampleIds((prev) => ({
      ...prev,
      ...Object.fromEntries(kinds.map((kind) => [kind, biosampleId])),
    }));
  }, [analysisSet.biosamples]);

  const getBiosampleForVariantType = useCallback((
    variantType: VariantType,
  ): IBiosample | undefined => {
    const variantTypeBiosampleMap: Record<VariantType, IBiosample | undefined> = {
      SNV: kindBiosamples.tumour,
      CNV: kindBiosamples.tumour,
      SV: kindBiosamples.tumour,
      GERMLINE_CNV: kindBiosamples.germline,
      GERMLINE_SNV: kindBiosamples.germline,
      GERMLINE_SV: kindBiosamples.germline,
      RNA_SEQ: kindBiosamples.rna,
      RNA_CLASSIFIER: kindBiosamples.rna,
      CYTOGENETICS: kindBiosamples.tumour,
      CYTOGENETICS_CYTOBAND: kindBiosamples.tumour,
      CYTOGENETICS_ARM: kindBiosamples.tumour,
      GERMLINE_CYTO_ARM: kindBiosamples.tumour,
      GERMLINE_CYTO: kindBiosamples.germline,
      GERMLINE_CYTO_CYTOBAND: kindBiosamples.germline,
      METHYLATION: kindBiosamples.methylation,
      METHYLATION_MGMT: kindBiosamples.methylation,
      METHYLATION_CLASSIFIER: kindBiosamples.methylation,
      METHYLATION_GENE: kindBiosamples.methylation,
      MUTATIONAL_SIG: kindBiosamples.tumour,
      HTS: getHtsBiosamples(analysisSet.biosamples || [])[0],
      HTS_COMBINATION: getHtsBiosamples(analysisSet.biosamples || [])?.[0],
      TMB: undefined,
      IMMUNE_PROFILE: undefined,
    };
    return variantTypeBiosampleMap[variantType];
  }, [analysisSet.biosamples, kindBiosamples]);

  const updateDemographics = useCallback((demo?: IPatientDemographics | null): void => {
    setDemographics(demo);
  }, []);

  const value = useMemo(() => ({
    analysisSet,
    setAnalysisSet,
    demographics,
    updateDemographics,
    primaryBiosample: getPrimaryBiosample(analysisSet.biosamples || []),
    tumourBiosample: kindBiosamples.tumour,
    germlineBiosample: kindBiosamples.germline,
    rnaBiosample: kindBiosamples.rna,
    methBiosample: kindBiosamples.methylation,
    htsBiosamples: getHtsBiosamples(analysisSet.biosamples || []),
    htsCultures,
    getBiosampleForVariantType,
    selectBiosample,
    onCultureChange: (
      biosampleId: string,
      screenName: string,
      newCulture: IHTSCulture,
    ) => (
      setHTSCultures((prev) => prev.map((c) => ({
        ...c,
        ...(
          c.biosampleId === biosampleId && c.screenName === screenName
            ? newCulture
            : {}
        ),
      })))
    ),
    purity,
    metrics,
    rnaseqMetrics,
    immunoprofile,
    molecularConfirmation,
    setMolecularConfirmation,
    leukemiaSubtypes,
    setLeukemiaSubtypes,
    isReadOnly,
    errorLoadingItems,
  }), [
    analysisSet,
    setAnalysisSet,
    demographics,
    updateDemographics,
    htsCultures,
    purity,
    metrics,
    rnaseqMetrics,
    immunoprofile,
    getBiosampleForVariantType,
    selectBiosample,
    kindBiosamples,
    molecularConfirmation,
    setMolecularConfirmation,
    leukemiaSubtypes,
    setLeukemiaSubtypes,
    isReadOnly,
    errorLoadingItems,
  ]);

  useEffect(() => {
    setErrorLoadingItems((prev) => (prev.length ? [] : prev));
    setSelectedBiosampleIds({});
  }, [analysisSetId]);

  useEffect(() => {
    if (analysisSetId) {
      zeroDashSdk
        .curation
        .analysisSets
        .getAnalysisSetById(analysisSetId)
        .then(setAnalysisSet);
    }
  }, [analysisSetId, zeroDashSdk.curation.analysisSets]);

  useEffect(() => {
    zeroDashSdk
      .curation
      .purity
      .getPurity({ analysisSetId })
      .then((resp) => {
        setErrorLoadingItems((prev) => prev.filter((item) => item !== 'purity'));
        setPurity(resp[0]);
      })
      .catch(() => {
        setPurity(undefined);
        setErrorLoadingItems((prev) => Array.from(new Set([...prev, 'purity'])));
        enqueueSnackbar('Cannot fetch purity, please try again', { variant: 'error' });
      });
  }, [analysisSetId, zeroDashSdk.curation.purity, enqueueSnackbar, setErrorLoadingItems]);

  useEffect(() => {
    if (analysisSetId) {
      zeroDashSdk
        .curation
        .molecularConfirmation
        .getMolecularConfirmation(analysisSetId)
        .then((resp) => {
          if (resp) {
            const {
              diagnosisSubtype,
              zero2ConfirmedSubtype,
              ...rest
            } = resp;
            setLeukemiaSubtypes({ diagnosisSubtype, zero2ConfirmedSubtype });
            setMolecularConfirmation(rest);
          } else {
            setLeukemiaSubtypes({ ...defaultLeukemiaSubtypes });
            setMolecularConfirmation({ ...defaultMolecularConfirmation });
          }
        });
    }
  }, [analysisSetId, zeroDashSdk.curation.molecularConfirmation]);

  useEffect(() => {
    if (analysisSet.biosamples) {
      Promise.all(
        analysisSet.biosamples?.map(
          (biosample) => zeroDashSdk
            .curation
            .metrics
            .getMetrics(biosample.biosampleId),
        ),
      ).then((resp) => {
        setMetrics(resp.filter((metric) => metric));
      });
    }
  }, [analysisSet.biosamples, zeroDashSdk.curation.metrics]);

  useEffect(() => {
    const rnaBiosample = analysisSet.biosamples?.find(
      (biosample) => biosample.biosampleType === 'rna',
    );
    if (rnaBiosample) {
      zeroDashSdk
        .curation
        .metrics
        .getImmunoprofile(rnaBiosample.biosampleId)
        .then((resp) => {
          setErrorLoadingItems((prev) => prev.filter((item) => item !== 'immunoprofile'));
          setImmunoprofile(resp);
        })
        .catch(() => {
          setImmunoprofile(undefined);
          setErrorLoadingItems((prev) => Array.from(new Set([...prev, 'immunoprofile'])));
          enqueueSnackbar('Cannot fetch immunoprofile, please try again', { variant: 'error' });
        });
      zeroDashSdk
        .curation
        .metrics
        .getRNASeqMetrics(rnaBiosample.biosampleId)
        .then((resp) => setRnaseqMetrics(resp));
    }
  }, [analysisSet.biosamples, zeroDashSdk.curation.metrics, enqueueSnackbar, setErrorLoadingItems]);

  useEffect(() => {
    if (analysisSet?.c1EventNum && analysisSet?.patientId) {
      zeroDashSdk.patient.getPatientDemographics(
        analysisSet.patientId,
        analysisSet.c1EventNum || undefined,
      )
        .then((res) => {
          setDemographics(res);
        })
        .catch(() => setDemographics(null));
    }
  }, [analysisSet.c1EventNum, analysisSet.patientId, zeroDashSdk.patient]);

  useEffect(() => {
    const htsBiosamples = getHtsBiosamples(analysisSet.biosamples || []);
    if (htsBiosamples?.length) {
      Promise.all(
        htsBiosamples.map((biosample) => (
          zeroDashSdk.hts.getHTSCulture(
            biosample.biosampleId,
          )
        )),
      ).then((htsResp) => {
        setHTSCultures(htsResp.flatMap((c) => c));
        setErrorLoadingItems((prev) => prev.filter((item) => item !== 'htsBiosample'));
      })
        .catch(() => {
          setHTSCultures([]);
          setErrorLoadingItems((prev) => Array.from(new Set([...prev, 'htsBiosample'])));
        });
    }
  }, [analysisSet.biosamples, zeroDashSdk.hts]);

  return (
    <AnalysisSetContext.Provider value={value}>
      {!analysisSet.analysisSetId && (
        <LoadingPage message="Fetching sample data..." />
      )}
      { children || <Outlet /> }
    </AnalysisSetContext.Provider>
  );
}
